import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { theme } from '../../styles/theme';

// Highlighting, as a feature, has been implemented via React Context with
// all rules related to it.

// Each element that needed to be highlighted at certain time should be:
// 1. Wrapped into HighlightingContextProvider or withHighlightingContext HOC
// 2. (obviously) be prepared to change its color with the help of HighlightedEntity
// component

// withHighlightingContext HOC guarantees that highlighting functionality
// will work properly. Consider using it first

// Components can simply highlight themselves, or they can inherit highlighting
// of parent components

// If component is not wrapped with HighlightingContextProvider or
// withHighlightingContext HOC, you will get default context value.
// Desired component will not be highlighted and application would not be broken

interface IHighlightingContextValue {
  highlighted: boolean;
  highlightColor: string;
  activateHighlighting: () => void;
  deactivateHighlighting: () => void;
  setHighlightColor: (color: string) => void;
  clearHighlightedByTimeout: (ms?: number) => void;
}

const DEFAULT_HIGHLIGHT_COLOR = theme.colors.contentHighlight;

const initialState: IHighlightingContextValue = {
  highlighted: false,
  highlightColor: DEFAULT_HIGHLIGHT_COLOR,
  activateHighlighting: () => null,
  deactivateHighlighting: () => null,
  setHighlightColor: () => null,
  clearHighlightedByTimeout: () => null,
};

// 3 seconds by default for delayed automatic clear highlighting
const DELAY_TIME = 3000;

interface IHighlightingContextProviderProps {
  children: ReactNode;
}

const HighlightingContext =
  createContext<IHighlightingContextValue>(initialState);

export const useHighlightingContext = () => useContext(HighlightingContext);

const HighlightingContextProvider = ({
  children,
}: IHighlightingContextProviderProps) => {
  const [highlighted, setHighlighted] = useState<boolean>(false);
  const [highlightColor, setHighlightColor] = useState<string>(
    DEFAULT_HIGHLIGHT_COLOR,
  );
  const timeoutId = useRef<NodeJS.Timeout>();

  // In case if parent element is highlighted, we need to highlight a nested too
  const {
    highlighted: parentHighlighted,
    highlightColor: parentHighlightColor,
  } = useHighlightingContext();

  const clearHighlightedByTimeout = useCallback((ms?: number) => {
    timeoutId.current = setTimeout(() => {
      setHighlighted(false);
    }, ms || DELAY_TIME);
  }, []);

  const contextValue = useMemo<IHighlightingContextValue>(
    () => ({
      highlighted: parentHighlighted || highlighted,
      highlightColor:
        parentHighlightColor !== DEFAULT_HIGHLIGHT_COLOR
          ? parentHighlightColor
          : highlightColor,
      activateHighlighting: () => setHighlighted(true),
      deactivateHighlighting: () => {
        setHighlightColor(DEFAULT_HIGHLIGHT_COLOR);
        setHighlighted(false);
      },
      setHighlightColor: (color: string) => {
        setHighlightColor(color);
      },
      clearHighlightedByTimeout,
    }),
    [
      clearHighlightedByTimeout,
      highlighted,
      highlightColor,
      parentHighlighted,
      parentHighlightColor,
    ],
  );

  useEffect(() => {
    return () => {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }
    };
  }, []);

  return (
    <HighlightingContext.Provider value={contextValue}>
      {children}
    </HighlightingContext.Provider>
  );
};

export function withHighlightingContext<T extends object>(
  WrappedComponent: React.ComponentType<T>,
): React.FC<T> {
  // displayName is used for React Dev Tools.
  const displayName =
    WrappedComponent.displayName || WrappedComponent.name || 'Component';

  const ComponentWithContext = (props: T) => {
    return (
      <HighlightingContextProvider>
        <WrappedComponent {...props} />
      </HighlightingContextProvider>
    );
  };

  ComponentWithContext.displayName = `withHighlightingContext(${displayName})`;
  return ComponentWithContext;
}
